import * as React from 'react';
import { withFormik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { withTranslation } from 'react-i18next';
import {Row, Col, Form, Button} from 'react-bootstrap';

import { Resident, StaffMember } from '../../../../store/models/domain';
import Image from '../../../components/Image';
import ResidentService from '../../../../services/ResidentService';
import moment from 'moment';

import profileAvatarIcon from "../../../../assets/images/profileAvatar.svg"
import "./styles.scss"

export interface FormValues extends Partial<Resident> {   
    preCleanNote?: string,
    postCleanNote?: string,
    roomNumber: string,
    cleanType: string
}

const ALERT_DEFS = [
  { code: 'S', bg: '#7B2FBE', text: '#ffffff' },
  { code: 'P', bg: '#7B2FBE', text: '#ffffff' },
  { code: 'V', bg: '#7B2FBE', text: '#ffffff' },
  { code: 'F', bg: '#1a1a1a', text: '#ffffff' },
  { code: 'E', bg: '#FFFF00', text: '#1a1a1a' },
];

const ResidentNoteForm = (props: any & FormikProps<FormValues>) => {
    const {
        // Formik HOC props
        values,
        staffMember,
        handleSubmit,
        // Form props        
        submitLabel,
        onCancel,
        onSaveHkNote,
        // Translate props
        t,
    }: any = props;

    const resident = values.resident;
    const [isEditingNote, setIsEditingNote] = React.useState<boolean>(false);
    const [hkNoteText, setHkNoteText] = React.useState<string>(resident.hkNote?.text || '');
    const [noteDate, setNoteDate] = React.useState<string>(resident.hkNote?.noteDate);
    const [hkMember, setHkMember] = React.useState<StaffMember>(resident.hkNote?.staffMember);
    
    const handleNoteBlur = async () => {
        // save the note, call the endpoint
        // only save of there's a change on the text
        if (resident.hkNote?.text !== hkNoteText) {
            onSaveHkNote(resident.id, hkNoteText)
            setNoteDate(moment().toString())
            setHkMember(staffMember)
        }
        setIsEditingNote(false)
    }

    const ALERT_MAP: Record<string, boolean> = {
        F: resident.fallRisk,
        E: resident.exitSeeking,
        S: resident.sexuallyAbusive,
        V: resident.verballyAbusive,
        P: resident.physicallyAggressive
    };

    // check if any of the alerts are active/true
    const hasAlerts = Object.values(ALERT_MAP).some(value => value === true);
    const activeAlerts = ALERT_DEFS.filter(alert => ALERT_MAP[alert.code]);

    return (
            <div className='room-states-main-container'>
            <Form onSubmit={(e) => { handleSubmit(e as React.FormEvent<HTMLFormElement>) }}>
                <div className="rsm-body">
                    {/* Left — resident photo */}
                    <div className="rsm-photo-col">
                        {resident?.photo ? (
                            <Image srcFile={resident.photo} hideBorder={true} className="rsm-photo"/>
                        ) : (
                        <div className="rsm-photo rsm-photo-placeholder">
                            <img src={profileAvatarIcon} alt="P" width={40} height={40}/>
                        </div>
                        )}
                    </div>

                    {/* Right — room info */}
                    <div className="rsm-info-col">
                        <div className="rsm-room-number">Room {values.roomNumber}</div>
                        <div className="rsm-resident-name">{ResidentService.displayName(resident)}</div>
                        <div className="rsm-clean-type">{values.cleanType || '—'}</div>

                        {hasAlerts && (
                            <div className="rsm-alerts-row">
                                <span className="rsm-alerts-label">{t('Resident alerts:')}</span>
                                <div className="rsm-badges">
                                    {activeAlerts.map(alert => (
                                        <div
                                            key={alert.code}
                                            className="rsm-badge"
                                            style={{ background: alert.bg, color: alert.text }}
                                        >
                                        {alert.code}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Notes row */}
                <div className="rsm-notes-row">

                    {/* Pre-clean note — hidden entirely when empty */}
                    {values.preCleanNote && (
                        <div className="rsm-note">
                            <span className="rsm-note-label">{t('Pre-Cleaning note:')}</span>
                            <span className="rsm-note-text">{values.preCleanNote}</span>
                        </div>
                    )}

                    {/* Housekeeper note — always visible */}
                    <div className="rsm-hk-note">
                        <span className="rsm-note-label">{t('Housekeeper note:')}</span>

                        {isEditingNote ? (
                            <textarea
                                className="rsm-hk-textarea"
                                value={hkNoteText}
                                autoFocus
                                onChange={e => setHkNoteText(e.target.value)}
                                onBlur={handleNoteBlur}
                            />
                       ) : hkNoteText ? (
                            <>
                                <div className="rsm-hk-note-saved">
                                    <span className="rsm-hk-note-text" style={{ whiteSpace: 'pre-wrap' }}>{hkNoteText}</span>
                                    <button
                                        className="rsm-hk-edit-btn"
                                        onClick={() => setIsEditingNote(true)}
                                    >
                                        <i className="bi bi-pencil-fill" />
                                    </button>
                                </div>
                                <span className="rsm-hk-attribution">
                                    <>{hkMember?.firstName} {hkMember?.lastName} - {moment(noteDate).format('MMM DD')}</>
                                </span>
                            </>
                        ) : (
                            <div
                                className="rsm-hk-note-empty"
                                onClick={() => setIsEditingNote(true)}
                            >
                                <span className="rsm-hk-hint">{t('Tap to add a note about this resident…')}</span>
                                <button
                                    className="rsm-hk-edit-btn"
                                    onClick={() => setIsEditingNote(true)}
                                >
                                    <i className="bi bi-pencil-fill" style={{ color: '#c8c6c4', fontSize: 14 }} />
                                </button>
                            </div>
                        )}
                    </div>
                    </div>
                
                <Row className='no-gutter'>
                    <Col sm={12} md={12} className='pt-4 d-flex justify-content-between'>                    
                        <button className="button-default swal2-cancel swal2-styled m-0" type="button" onClick={() => { onCancel(values) }}>{t("Cancel")}</button>
                        <Button variant="swal2-styled button-default secondary-fill m-0" type="submit" >{submitLabel}</Button>
                    </Col>
                </Row>
            </Form>
        </div>
        
    );
};

const validate = withFormik<any, FormValues>({
    mapPropsToValues: props => ({
        id: props.values?.id,
        resident: props.values.resident,
        preCleanNote:  props.values?.preCleanNote,
        postCleanNote:  props.values?.postCleanNote,
        roomNumber:  props.values?.roomNumber,
        cleanType:  props.values?.cleanType,
        hkNote:  props.values?.hkNote   
    }),

    validationSchema: (props: any) => {
        return Yup.object().shape({            
    })},

    handleSubmit: (values, { props }) => {
        props.onSubmit(values);
    },
});

const translate = withTranslation();
export default translate(validate(ResidentNoteForm));
